import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { CollapsibleCard } from '@/components/ui/CollapsibleCard';
import { RowActionsMenu } from '@/components/ui/RowActionsMenu';
import { SortableHandle } from '@/components/ui/SortableList';
import { useSortableMoves } from '@/components/ui/sortableItemContext';

import { contentKind, itemCountLabel, type ArcSection } from './arcTypes';
import { KindDot, SectionIcon } from './sectionLook';
import { SectionItemsTable } from './SectionItemsTable';
import type { SectionHandlers } from './SectionsList';

/** The two sections the wireframe draws open; the rest start folded. */
const OPEN_BY_DEFAULT = new Set(['FEATURED', 'CAMPAIGNS']);

interface SectionCardProps extends SectionHandlers {
  section: ArcSection;
  readOnly: boolean;
  /** Why no more content can be added here, or null. */
  addReason: string | null;
  /** Why this hidden section cannot be shown, or null (B26). */
  showReason: string | null;
}

/**
 * One section of the ARC, as the wireframe draws it (Part 2, page 4): its
 * drag handle, the icon of what it holds, its name, a dot and words for
 * standard or mixed content, how much it holds, whether app users see it,
 * Collapse or Expand, and Actions.
 *
 * Every section can be renamed, hidden, shown or deleted, standard ones
 * included (E.2): an organization that has no guides can drop the Guides
 * section, and add it back from New section. Move up and Move down are in
 * Actions, for anybody who cannot drag.
 */
export function SectionCard({
  section,
  readOnly,
  addReason,
  showReason,
  onSetVisible,
  onDelete,
  onRename,
  onRemoveItem,
  onMoveItem,
  onReorderItems,
  onAddContent,
}: SectionCardProps) {
  const prefix = `arc-builder-section-${section.id}`;
  const kind = contentKind(section);
  const moves = useSortableMoves();

  return (
    <CollapsibleCard
      title={section.name}
      muted={!section.visible}
      toggle="button"
      defaultExpanded={OPEN_BY_DEFAULT.has(section.standard_type ?? '')}
      testId={`${prefix}-card`}
      leading={<SortableHandle />}
      icon={<SectionIcon section={section} />}
      meta={
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <KindDot mixed={kind.mixed} />
            <Typography variant="body2" data-testid={`${prefix}-kind-chip`}>
              {kind.label}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            data-testid={`${prefix}-count`}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {itemCountLabel(section.items.length)}
          </Typography>
          <Typography
            variant="body2"
            color={section.visible ? 'primary' : 'error'}
            data-testid={`${prefix}-visibility-chip`}
            sx={{ fontWeight: 600 }}
          >
            {section.visible ? 'Visible' : 'Hidden'}
          </Typography>
        </>
      }
      actions={
        <RowActionsMenu
          rowLabel={section.name}
          testId={`${prefix}-actions`}
          trigger="button"
          actions={[
            {
              id: 'move-up',
              label: 'Move up',
              onSelect: moves.moveUp,
              disabled: !moves.canMoveUp,
            },
            {
              id: 'move-down',
              label: 'Move down',
              onSelect: moves.moveDown,
              disabled: !moves.canMoveDown,
            },
            {
              id: 'rename',
              label: 'Rename',
              onSelect: () => onRename(section),
              disabled: readOnly,
            },
            section.visible
              ? {
                  id: 'hide',
                  label: 'Hide',
                  onSelect: () => onSetVisible(section, false),
                  disabled: readOnly,
                }
              : {
                  id: 'show',
                  label: 'Show',
                  onSelect: () => onSetVisible(section, true),
                  disabled: readOnly || showReason !== null,
                  hint: showReason ?? undefined,
                },
            {
              id: 'delete',
              label: 'Delete',
              onSelect: () => onDelete(section),
              disabled: readOnly,
            },
          ]}
        />
      }
    >
      <SectionItemsTable
        section={section}
        prefix={prefix}
        readOnly={readOnly}
        addReason={addReason}
        onRemove={(item) => onRemoveItem(section, item)}
        onMove={(item) => onMoveItem(section, item)}
        onReorder={(ids) => onReorderItems(section, ids)}
        onAdd={() => onAddContent(section)}
      />
    </CollapsibleCard>
  );
}
